import { ReactRenderer } from "@tiptap/react";
import tippy from "tippy.js";
import MentionList from "./MentionList";

export const createSuggestion = (getQueryData, triggerChar = "@") => {
  return {
    items: ({ query }) => {
      // Get fresh data each time
      const queryData = getQueryData();

      // Check if this is actually a function that wasn't called
      if (typeof queryData === "function") {
        console.error(
          "queryData is a function! getQueryData wasn't called properly",
        );
        return [];
      }

      // Check if queryData is undefined or null
      if (!queryData) {
        console.error("queryData is null or undefined");
        return [];
      }

      if (triggerChar === "@") {
        // Handle user mentions

        // Still loading - show loading indicator
        if (queryData?.isLoading) {
          return [
            {
              id: "loading",
              name: "Loading users...",
              type: "loading",
            },
          ];
        }

        // Error state
        if (queryData?.isError) {
          return [
            {
              id: "error",
              name: "Error loading users",
              type: "error",
            },
          ];
        }

        // No data or not successful yet
        if (!queryData?.isSuccess || !queryData?.data) {
          return [];
        }

        const currentUserData = queryData.data;

        if (currentUserData.length === 0) {
          return [
            {
              id: "no-users",
              name: "No users found",
              type: "info",
            },
          ];
        }

        const data = currentUserData
          .filter((user) => {
            if (!user || !user.name) return false;
            return user.name.toLowerCase().includes(query.toLowerCase());
          })
          .map((user) => ({
            id: user.ID || user.id,
            name: user.name,
            type: "user",
          }))
          .slice(0, 5);

        return data;
      }
      if (triggerChar === "/") {
        // Still loading - show loading indicator
        if (queryData?.isLoading) {
          return [
            {
              id: "loading",
              name: "Loading sections...",
              type: "loading",
            },
          ];
        }

        // Error state
        if (queryData?.isError) {
          return [
            {
              id: "error",
              name: "Error loading sections",
              type: "error",
            },
          ];
        }

        // No data or not successful yet
        if (!queryData?.isSuccess || !queryData?.data) {
          return [];
        }

        const currentSectionData = queryData.data.sections || [];

        if (currentSectionData.length === 0) {
          return [
            {
              id: "no-sections",
              name: "No sections found",
              type: "info",
            },
          ];
        }

        const data = currentSectionData
          .filter((section) => {
            if (!section || !section.title) return false;
            return section.title.toLowerCase().includes(query.toLowerCase());
          })
          .map((section) => ({
            id: section.manualSectionID,
            name: section.title,
            type: "section",
          }))
          .slice(0, 5);

        // If no matches, show "no results"
        if (data.length === 0 && query.length > 0) {
          return [
            {
              id: "no-match",
              name: `No sections matching "${query}"`,
              type: "info",
            },
          ];
        }

        return data;
      }
      return [];
    },

    render: () => {
      let component;
      let popup;

      return {
        onStart: (props) => {
          component = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
          });

          if (!props.clientRect) {
            return;
          }

          popup = tippy("body", {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "bottom-start",
          });
        },

        onUpdate(props) {
          if (component) {
            component.updateProps(props);
          }

          if (!props.clientRect || !popup || !popup[0]) {
            return;
          }

          popup[0].setProps({
            getReferenceClientRect: props.clientRect,
          });
        },

        onKeyDown(props) {
          if (props.event.key === "Escape") {
            if (popup && popup[0]) {
              popup[0].hide();
            }
            return true;
          }

          return component?.ref?.onKeyDown(props);
        },

        onExit() {
          if (popup && popup[0]) {
            popup[0].destroy();
          }
          if (component) {
            component.destroy();
          }
        },
      };
    },
  };
};

export default createSuggestion;
